// File: src/services/api.ts

import {
    AIService,
    Marketplace,
    Product,
    Project,
    PromptRenderRequest,
    PromptRenderResponse,
    PromptTemplate,
    PromptTemplateCreate,
    ResearchIteration,
    User,
    ImageGeneration,
    DescriptionRequest,
    DescriptionGenerationResponse,
    VideoGeneration,
    VideoGenerationCreate,
    FunnelGeneration,
    FunnelGenerationRequest,
    FunnelSlideRegenerateRequest,
    FunnelStatsResponse
} from '../types';


const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const getAuthHeaders = (isFormData: boolean = false): HeadersInit => {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (!isFormData) headers['Content-Type'] = 'application/json';
    return headers;
};

const handleResponse = async <T>(res: Response): Promise<T> => {
    if (!res.ok) {
        if (res.status === 401) localStorage.removeItem('token');
        const errorData = await res.json().catch(() => ({detail: `HTTP error! status: ${res.status}`}));
        throw new Error(errorData.detail || 'An unknown API error occurred');
    }
    return res.json();
};

// --- AUTH ---
export const authWithGoogle = async (credential: string): Promise<{ access_token: string }> => {
    const res = await fetch(`${API_URL}/auth/google`, {
        method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({credential})
    });
    return handleResponse(res);
};

// --- USER ---
export const fetchUserProfile = async (): Promise<User> => {
    const res = await fetch(`${API_URL}/users/me`, {headers: getAuthHeaders()});
    return handleResponse(res);
};

export const fetchAllUsers = async (): Promise<User[]> => {
    const res = await fetch(`${API_URL}/admin/users`, {headers: getAuthHeaders()});
    return handleResponse(res);
};

export const updateUser = async (userId: number, data: Partial<User>): Promise<User> => {
    const res = await fetch(`${API_URL}/users/${userId}`, {
        method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data)
    });
    return handleResponse(res);
};

// --- PRODUCTS ---
export const getProducts = async (): Promise<Product[]> => {
    const res = await fetch(`${API_URL}/products/`, {headers: getAuthHeaders()});
    return handleResponse(res);
};

export const createProduct = async (productData: Partial<Omit<Product, 'id' | 'created_at' | 'updated_at'>>): Promise<Product> => {
    const res = await fetch(`${API_URL}/products/`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(productData)
    });
    return handleResponse(res);
};


export const updateProduct = async (productId: number, productData: Partial<Product>): Promise<Product> => {
    const res = await fetch(`${API_URL}/products/${productId}`, {
        method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(productData)
    });
    return handleResponse(res);
};

export const getProductById = async (productId: number): Promise<Product> => {
    const res = await fetch(`${API_URL}/products/${productId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
};

export const uploadImages = async (files: FileList): Promise<{ url: string }[]> => {
    const formData = new FormData();
    Array.from(files).forEach(file => formData.append('files', file));
    const res = await fetch(`${API_URL}/products/upload-images`, {
        method: 'POST', headers: getAuthHeaders(true), body: formData
    });
    const data = await handleResponse<{ files: { url: string }[] }>(res);
    return data.files;
};

// --- PROJECTS ---
export const getProjectsByProductId = async (productId: number): Promise<Project[]> => {
    const res = await fetch(`${API_URL}/projects/?product_id=${productId}`, {headers: getAuthHeaders()});
    return handleResponse(res);
};

export const createProject = async (projectData: {
    name: string;
    product_id: number;
    marketplace_id: number
}): Promise<Project> => {
    const res = await fetch(`${API_URL}/projects`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(projectData)
    });
    return handleResponse(res);
};

// --- RESEARCH ---
export const getResearchResult = async (productId: number, researchId: number): Promise<ResearchIteration> => {
    const res = await fetch(`${API_URL}/products/${productId}/research/${researchId}?include_prompt=true`, {headers: getAuthHeaders()});
    return handleResponse(res);
};

export const startResearch = async (
    params: {
        productId: number,
        ai_service: string,
        marketplaceCode?: string,
        prompt_template_id?: number | null
        reference_image_url?: string | null; // <<< ИЗМЕНЕНИЕ 1: Добавляем новое поле в параметры
    }
): Promise<{ id: number; status: string }> => {
    // <<< ИЗМЕНЕНИЕ 2: Извлекаем новое поле из параметров
    const {productId, ai_service, marketplaceCode, prompt_template_id, reference_image_url} = params;

    const url = `${API_URL}/products/${productId}/research/start?include_prompt=true` +
        (marketplaceCode ? `&marketplace=${marketplaceCode}` : '');

    const res = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
            ai_service,
            prompt_template_id: prompt_template_id || null,
            research_settings: {},
            target_marketplaces: marketplaceCode ? [marketplaceCode] : [],
            reference_image_url: reference_image_url, // <<< ИЗМЕНЕНИЕ 3: Добавляем поле в тело запроса
        }),
    });
    return handleResponse(res);
};

export const getResearchGenerationsForProduct = async (productId: number): Promise<ResearchIteration[]> => {
    const res = await fetch(`${API_URL}/products/${productId}/research/`, {headers: getAuthHeaders()});
    return handleResponse(res);
};

// --- MARKETPLACE ---
export const getMarketplaces = async (): Promise<Marketplace[]> => {
    const res = await fetch(`${API_URL}/marketplaces/`, {headers: getAuthHeaders()});
    return handleResponse(res);
};

export const createMarketplace = async (data: Partial<Omit<Marketplace, 'id' | 'created_at' | 'updated_at'>>): Promise<Marketplace> => {
    const res = await fetch(`${API_URL}/marketplaces/`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const updateMarketplace = async (id: number, data: Partial<Marketplace>): Promise<Marketplace> => {
    const res = await fetch(`${API_URL}/marketplaces/${id}`, {
        method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const deleteMarketplace = async (id: number): Promise<void> => {
    const res = await fetch(`${API_URL}/marketplaces/${id}`, {method: 'DELETE', headers: getAuthHeaders()});
    if (!res.ok) await handleResponse(res);
};

// --- AI SERVICES ---
export const fetchAIServices = async (taskType?: string): Promise<AIService[]> => {
    const params = taskType ? `?task_type=${taskType}` : '';
    const res = await fetch(`${API_URL}/ai-services/${params}`, {headers: getAuthHeaders()});
    return handleResponse(res);
};

export const createAIService = async (data: Partial<Omit<AIService, 'id' | 'created_at' | 'updated_at'>>): Promise<AIService> => {
    const res = await fetch(`${API_URL}/ai-services/`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const updateAIService = async (id: number, data: Partial<AIService>): Promise<AIService> => {
    const res = await fetch(`${API_URL}/ai-services/${id}`, {
        method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const deleteAIService = async (id: number): Promise<void> => {
    const res = await fetch(`${API_URL}/ai-services/${id}`, {method: 'DELETE', headers: getAuthHeaders()});
    if (!res.ok) await handleResponse(res);
};

// --- PROMPT TEMPLATES ---
export const getPromptTemplates = async (category?: string): Promise<PromptTemplate[]> => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    const res = await fetch(`${API_URL}/prompts/?${params}`, {headers: getAuthHeaders()});
    return handleResponse(res);
};

export const getPromptTemplate = async (id: number): Promise<PromptTemplate> => {
    const res = await fetch(`${API_URL}/prompts/${id}`, {headers: getAuthHeaders()});
    return handleResponse(res);
};

export const createPromptTemplate = async (data: PromptTemplateCreate): Promise<PromptTemplate> => {
    const res = await fetch(`${API_URL}/prompts/`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const updatePromptTemplate = async (id: number, data: Partial<PromptTemplateCreate>): Promise<PromptTemplate> => {
    const res = await fetch(`${API_URL}/prompts/${id}`, {
        method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data)
    });
    return handleResponse(res);
};

export const renderPrompt = async (request: PromptRenderRequest): Promise<PromptRenderResponse> => {
    const res = await fetch(`${API_URL}/prompts/render`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(request)
    });
    return handleResponse(res);
};

// --- IMAGE GENERATION ---
export const startPromptGeneration = async (
    productId: number,
    body: {
        prompt_template_id: number;
        ai_service_used: string;
        image_ai_service_used: string;
        language?: string;
        num_images: number;
        extra_instructions?: string;
        reference_image_url: string;
    }
): Promise<{ id: number; status: string }> => {
    const res = await fetch(`${API_URL}/products/${productId}/image-generations/start`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
    });
    return handleResponse(res);
};

export const startImageGeneration = async (productId: number, generationId: number): Promise<{ id: number; status: string }> => {
    const res = await fetch(`${API_URL}/products/${productId}/image-generations/${generationId}/generate`, {
        method: 'POST', headers: getAuthHeaders(),
    });
    return handleResponse(res);
};

export const getGenerationStatus = async (productId: number, generationId: number): Promise<ImageGeneration> => {
    const res = await fetch(`${API_URL}/products/${productId}/image-generations/${generationId}?include_prompt=true`, {headers: getAuthHeaders()});
    return handleResponse(res);
};

export const getGenerationsForProduct = async (productId: number): Promise<ImageGeneration[]> => {
    const res = await fetch(`${API_URL}/products/${productId}/image-generations/`, {headers: getAuthHeaders()});
    return handleResponse(res);
};

// --- DESCRIPTION GENERATION ---
export const startDescriptionGeneration = async (productId: number, body: DescriptionRequest): Promise<{ id: number; status: string }> => {
    const res = await fetch(`${API_URL}/products/${productId}/description-generations/start`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
    });
    return handleResponse(res);
};

export const getDescriptionGeneration = async (productId: number, generationId: number): Promise<DescriptionGenerationResponse> => {
    const res = await fetch(`${API_URL}/products/${productId}/description-generations/${generationId}?include_prompt=true`, {
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
};

export const getDescriptionGenerationsForProduct = async (productId: number): Promise<DescriptionGenerationResponse[]> => {
    const res = await fetch(`${API_URL}/products/${productId}/description-generations/`, {
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
};

export const getAllDescriptionGenerations = async (limit: number = 50): Promise<DescriptionGenerationResponse[]> => {
    const res = await fetch(`${API_URL}/products/user/descriptions?limit=${limit}`, {headers: getAuthHeaders()});
    return handleResponse(res);
};


export const startVideoPromptGeneration = async (
    productId: number,
    requestData: VideoGenerationCreate
): Promise<VideoGeneration> => {
    const res = await fetch(
        `${API_URL}/products/${productId}/video-generations/start`,
        {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(requestData),
        }
    );
    return handleResponse(res);
};

export const startVideoGenerationFromPrompt = async (
    productId: number,
    generationId: number
): Promise<VideoGeneration> => {
    const res = await fetch(
        `${API_URL}/products/${productId}/video-generations/${generationId}/generate-video`,
        {
            method: 'POST',
            headers: getAuthHeaders(),
        }
    );
    return handleResponse(res);
};

export const getSingleVideoGeneration = async (
    productId: number,
    generationId: number
): Promise<VideoGeneration> => {
    const res = await fetch(`${API_URL}/products/${productId}/video-generations/${generationId}?include_prompt=true`, {
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
};

export const getVideoGenerationsForProduct = async (
    productId: number,
    limit: number = 10
): Promise<VideoGeneration[]> => {
    const res = await fetch(`${API_URL}/products/${productId}/video-generations/?limit=${limit}`, {
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
};

// === FUNNEL GENERATION API ===

export const startFunnelGeneration = async (
    productId: number,
    request: FunnelGenerationRequest
): Promise<{ id: number; status: string }> => {
    const res = await fetch(`${API_URL}/products/${productId}/funnel-generations/start`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
    });
    return handleResponse(res);
};

export const triggerFunnelSlideGeneration = async (
    productId: number,
    generationId: number
): Promise<{ id: number; status: string }> => {
    const res = await fetch(`${API_URL}/products/${productId}/funnel-generations/${generationId}/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
};

export const getFunnelGenerationStatus = async (
    productId: number,
    generationId: number
): Promise<FunnelGeneration> => {
    const res = await fetch(`${API_URL}/products/${productId}/funnel-generations/${generationId}?include_prompt=true`, {
        headers: getAuthHeaders()
    });
    return handleResponse(res);
};

export const getFunnelGenerationsForProduct = async (
    productId: number,
    filters?: {
        status?: string;
        funnel_type?: string;
        target_marketplace?: string;
    }
): Promise<FunnelGeneration[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.funnel_type) params.append('funnel_type', filters.funnel_type);
    if (filters?.target_marketplace) params.append('marketplace', filters.target_marketplace);

    const res = await fetch(`${API_URL}/products/${productId}/funnel-generations/?${params.toString()}`, {
        headers: getAuthHeaders()
    });
    return handleResponse(res);
};

export const regenerateFunnelSlide = async (
    productId: number,
    generationId: number,
    slidePosition: number,
    request: FunnelSlideRegenerateRequest
): Promise<FunnelGeneration> => {
    const res = await fetch(`${API_URL}/products/${productId}/funnel-generations/${generationId}/slides/${slidePosition}/regenerate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
    });
    return handleResponse(res);
};

export const getFunnelUserStats = async (): Promise<FunnelStatsResponse> => {
    const res = await fetch(`${API_URL}/funnel-stats/me`, {
        headers: getAuthHeaders()
    });
    return handleResponse(res);
};

export const cancelFunnelGeneration = async (
    productId: number,
    generationId: number
): Promise<{ message: string }> => {
    const res = await fetch(`${API_URL}/products/${productId}/funnel-generations/${generationId}/cancel`, {
        method: 'POST',
        headers: getAuthHeaders(),
    });
    return handleResponse(res);
};
